import { useState, useEffect } from "react";
import { enablePushNotifications } from "../notifications/serviceWorkerClient";
import './PushPermissionBanner.css';

const DISMISSED_KEY = 'notif-banner-dismissed';
const SUCCESS_DWELL_MS = 4000; // 4 seconds for success confirmation

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    /* Storage may throw in private-browse modes or if full — fall open. */
  }
  return false;
}

function writeDismissed(): void {
  try {
    localStorage.setItem(DISMISSED_KEY, '1');
  } catch {
    /* ignore persistence failure — dismissed state still hides the banner */
  }
}

// Computed once, synchronously, as the initial value of `shouldRender`
// (see the lazy useState initializer below) rather than in a mount effect.
// An effect runs AFTER the first paint, so the banner would render its
// initial "not shown" state at t=0 and then flash in a frame later once
// the effect commits — visible as a one-frame pop-in on every page load
// even though the answer (Notification API present? dismissed? permission
// already decided?) is available synchronously. Wrapped in the same
// try/catch as readDismissed() — `'Notification' in window` and
// `Notification.permission` can both throw in restrictive/private-browse
// contexts, and this must fail open (render nothing) just like readDismissed.
function computeInitialShouldRender(): boolean {
  try {
    if (!('Notification' in window)) {
      return false;
    }
    if (readDismissed()) {
      return false;
    }
    if (Notification.permission === 'granted' || Notification.permission === 'denied') {
      // Already has a permission decision, don't show the banner
      return false;
    }
    // Default permission state — show the banner
    return true;
  } catch {
    return false;
  }
}

export function PushPermissionBanner() {
  const [shouldRender, setShouldRender] = useState(computeInitialShouldRender);
  const [state, setState] = useState<"idle" | "on" | "off">("idle");

  // Auto-hide after "on" state is set (after dwell time for visibility)
  useEffect(() => {
    if (state === "on") {
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, SUCCESS_DWELL_MS);
      return () => clearTimeout(timer);
    }
  }, [state]);

  if (!shouldRender) {
    return null;
  }

  const handleDismiss = () => {
    writeDismissed();
    setShouldRender(false);
  };

  // Show success confirmation
  if (state === "on") {
    return (
      <div className="notif-banner notif-banner--success">
        <span className="notif-banner-text">Notifications enabled ✓</span>
        <button
          className="notif-banner-btn-dismiss"
          onClick={handleDismiss}
          aria-label="Dismiss notifications banner"
        >
          ×
        </button>
      </div>
    );
  }

  return (
    <div className="notif-banner">
      <div className="notif-banner-content">
        <span className="notif-banner-text">
          Get notified about trading events?
        </span>
        <button
          className="notif-banner-btn-enable"
          onClick={async () => setState((await enablePushNotifications()) ? "on" : "off")}
        >
          Enable
        </button>
        {state === "off" && <span className="notif-banner-error"> (not enabled)</span>}
      </div>
      <button
        className="notif-banner-btn-dismiss"
        onClick={handleDismiss}
        aria-label="Dismiss notifications banner"
      >
        ×
      </button>
    </div>
  );
}
